// === TimelineElementNode === read-only React Flow node used by the docked
// CanvasWorkflowPanel (Phase C Item 2). One node per non-InteractivePlugin
// scene box (HotspotImages, BasicText, Image, Video, …). Visually neutral
// (gray header, smaller font) so authors instantly distinguish it from the
// editable InteractivePlugin sub-item nodes (ElementNode). Carries no
// editable outflow handles — these are placeholders for the auto-edge that
// connects the latest timeline element to the first InteractivePlugin item.
//
// The node still exposes a single "target" handle on the left so the
// "in sequence" auto-edge can land, and a single "source" handle on the
// right (no label) so future authoring flows (e.g. dragging from a timeline
// element to an interactive item) have an anchor. Both handles are hidden
// visually by CSS to keep the placeholder look.

import React from "react";
import { Handle, Position } from "@xyflow/react";
import "./TimelineElementNode.scss";

// Author-facing display labels for timeline plugin kinds. Used as the fallback
// when data.label is missing so the header reads "Hotspot Images" instead of
// the bare PascalCase identifier. Unknown kinds fall back to splitCamelCase or
// the raw string. Exported so buildElementGraph can reuse the same map when it
// computes data.label up-front (single source of truth — TimelineElementNode
// is the rendering authority for kind→friendly-name mapping).
export const KIND_LABEL = {
  BasicText: "Basic Text",
  BasicImage: "Basic Image",
  BasicVideo: "Basic Video",
  BasicAudio: "Basic Audio",
  BasicPDF: "Basic PDF",
  TextPlayer: "Text Player",
  Title: "Title",
  HotspotImages: "Hotspot Images",
  HotspotsImage: "Hotspots Image",
  Image: "Image",
  EnrichedPlayer: "Enriched Player",
  EnrichedAudio: "Enriched Audio",
  EnrichedPDF: "Enriched PDF",
  AudioCue: "Audio Cue",
  Video: "Video",
  DataTable: "Data Table",
  DragDrop: "Drag & Drop",
  Ordering: "Ordering",
  Matching: "Matching",
  TrueFalse: "True / False",
  YesNo: "Yes / No",
  MultipleAnswer: "Multiple Answer",
  MultipleChoice: "Multiple Choice",
  FillInBlank: "Fill in the Blank",
  GraficaD3: "D3 Chart",
  PDF: "PDF",
  Visor3D: "3D Viewer",
  VirtualTour: "Virtual Tour",
  Characters: "Characters",
  AnimationOptions: "Animation Options",
  OneClick: "One Click",
  AtomicBox: "Atomic Element",
};

// Split PascalCase / camelCase into spaced Title Case ("HotspotImages" →
// "Hotspot Images"). Exported so buildElementGraph applies the same heuristic
// when no explicit KIND_LABEL entry exists, keeping the rendered header and
// the data layer label in sync for unknown kinds.
export function splitCamelCase(s) {
  return (s || "").replace(/([a-z])([A-Z])/g, "$1 $2").replace(/^./, (c) => c.toUpperCase());
}

// Per-pluginId Material icon — kept inline so the node has no external icon
// dependency. Falls back to a generic glyph for unknown plugins. Mapping is
// curated to match the Studio timeline's PLUGIN_ICONS at
// _editor/components/timeline/IdeaTimeline/index.jsx:73-93 in spirit, but
// uses Material icon strings since the canvas_workflow surface already
// renders Material icons in ElementNode.
const KIND_ICON = {
  BasicText: "text_fields",
  TextPlayer: "text_fields",
  Title: "title",
  HotspotImages: "image",
  HotspotsImage: "image",
  Image: "image",
  EnrichedPlayer: "play_circle",
  EnrichedAudio: "music_note",
  BasicAudio: "music_note",
  AudioCue: "campaign",
  Video: "movie",
  DataTable: "table_chart",
  DragDrop: "drag_indicator",
  Ordering: "swap_vert",
  Matching: "compare_arrows",
  TrueFalse: "rule",
  YesNo: "thumbs_up_down",
  MultipleAnswer: "check_box",
  MultipleChoice: "radio_button_checked",
  FillInBlank: "edit",
  GraficaD3: "bar_chart",
  PDF: "picture_as_pdf",
  BasicPDF: "picture_as_pdf",
  EnrichedPDF: "picture_as_pdf",
  Visor3D: "view_in_ar",
  VirtualTour: "panorama",
  Characters: "person",
  AnimationOptions: "auto_fix_high",
  OneClick: "ads_click",
};

function truncate(text, max = 36) {
  if (!text) return "";
  const str = String(text);
  return str.length <= max ? str : `${str.slice(0, max - 1)}…`;
}

export default function TimelineElementNode({ data, selected }) {
  const kind = (data && data.kind) || "";
  // Prefer the data layer's authored label, then the friendly kind label,
  // then a camelCase-split of the raw kind, then the bare kind. The friendly
  // map keeps the header readable when the caller didn't pass a label (older
  // paths) without ever uppercasing.
  const label = truncate(
    (data && data.label) || KIND_LABEL[kind] || splitCamelCase(kind) || "Element"
  );
  const timingText = (data && data.timingText) || "";
  const icon = KIND_ICON[kind] || "category";
  const visible = data ? data.visible !== false : true;
  const classes = ["canvas-wf-tl-node"];
  if (selected) classes.push("canvas-wf-tl-node--selected");
  if (!visible) classes.push("canvas-wf-tl-node--hidden");

  return (
    <div
      className={classes.join(" ")}
      data-kind={kind}
      data-box-id={(data && data.boxId) || ""}
      title={label}
    >
      <Handle
        id="in"
        type="target"
        position={Position.Left}
        className="canvas-wf-tl-node__handle"
      />
      <div className="canvas-wf-tl-node__head">
        <i className="material-icons">{icon}</i>
        <span className="canvas-wf-tl-node__label">{label}</span>
      </div>
      <div className="canvas-wf-tl-node__timing">{timingText}</div>
      <Handle
        id="out"
        type="source"
        position={Position.Right}
        className="canvas-wf-tl-node__handle"
      />
    </div>
  );
}
